import { LandingBlock, LandingBlockType, LandingPage } from '../types'
import { slugify } from '../utils/format'
import { blockDefinition } from '../data/blockLibrary'
import { LandingTemplateId, templateBlocks } from '../data/landingTemplates'
import { supabase } from './supabaseClient'
import { blockRow, landingConfig, landingRow, toBlock, toLanding } from './mappers'
import { getDb, refresh, saveConfig } from './store'
import { newId } from './authService'
import { logFailure, logSuccess } from '../utils/diagnostics'

export function listLandings(organizationId: string): LandingPage[] {
  return getDb().landings.filter((l) => l.organizationId === organizationId)
}

export function getLanding(landingId: string): LandingPage | undefined {
  return getDb().landings.find((l) => l.id === landingId)
}

export function getLandingBySlug(slug: string): LandingPage | undefined {
  return getDb().landings.find((l) => l.slug === slug)
}

export function newBlock(type: LandingBlockType): LandingBlock {
  const definition = blockDefinition(type)
  return {
    id: newId('block'),
    type,
    visible: true,
    spacing: 'normal',
    config: JSON.parse(JSON.stringify(definition.defaultConfig)),
  }
}

/** Descarga una landing publicada (visitante anónimo). */
export async function fetchPublicLanding(
  slug: string,
): Promise<LandingPage | null> {
  const { data, error } = await supabase
    .from('landing_pages')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .is('deleted_at', null)
    .maybeSingle()

  if (error) {
    // Un error de RLS o de consulta se veía antes como «página no existe».
    logFailure('fetchPublicLanding', error, { slug })
    return null
  }
  if (!data) {
    logFailure(
      'fetchPublicLanding',
      {
        message: `No hay ninguna landing publicada con el slug "${slug}". Comprueba que está publicada y que el slug coincide.`,
      },
      { slug },
    )
    return null
  }

  const [{ data: blocks }, { data: settings }] = await Promise.all([
    supabase
      .from('landing_blocks')
      .select('*')
      .eq('landing_id', data.id)
      .order('position'),
    supabase
      .from('organization_settings')
      .select('value')
      .eq('organization_id', data.organization_id)
      .eq('key', `landing_config:${data.id}`)
      .maybeSingle(),
  ])

  logSuccess('fetchPublicLanding', {
    slug,
    landingId: data.id,
    organizationId: data.organization_id,
    formId: data.form_id,
    bloques: (blocks ?? []).length,
  })

  return toLanding(data, settings?.value ?? {}, (blocks ?? []).map(toBlock))
}

export async function createLanding(
  organizationId: string,
  name: string,
  formId?: string,
  template: LandingTemplateId = 'con_video',
): Promise<LandingPage> {
  const landing: LandingPage = {
    id: newId('landing'),
    organizationId,
    formId,
    name,
    slug: slugify(name) || `landing-${Date.now().toString(36)}`,
    status: 'borrador',
    views: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    pageConfig: {
      theme: 'blanco_premium',
      primaryColor: '#3D8BFF',
      fontFamily: 'Inter',
      // Los bloques provienen de la plantilla elegida, con ids UUID válidos.
      blocks: templateBlocks(template, () => newId()),
      thankYou: {
        title: 'Hemos recibido tu solicitud.',
        text: 'Nos pondremos en contacto contigo lo antes posible.',
        showCalendly: false,
        showWhatsapp: false,
        showHome: true,
      },
    },
  }
  const { error } = await supabase.from('landing_pages').insert(landingRow(landing))
  if (error) throw new Error(error.message)
  await saveLanding(landing)
  return landing
}

/** Guarda la landing completa: cabecera, configuración y bloques. */
export async function saveLanding(landing: LandingPage): Promise<void> {
  // Se usa upsert para que también funcione si la fila no existía.
  const { error } = await supabase
    .from('landing_pages')
    .upsert(landingRow(landing), { onConflict: 'id' })
  if (error) {
    throw new Error(`No se ha podido guardar la landing: ${error.message}`)
  }

  try {
    await saveConfig(
      landing.organizationId,
      'landing',
      landing.id,
      landingConfig(landing),
    )
  } catch (err) {
    throw new Error(
      `No se ha podido guardar la configuración de la landing: ${
        err instanceof Error ? err.message : 'error desconocido'
      }`,
    )
  }

  const { data: existing, error: readError } = await supabase
    .from('landing_blocks')
    .select('id')
    .eq('landing_id', landing.id)
  if (readError) {
    throw new Error(`No se han podido leer los bloques: ${readError.message}`)
  }
  const keep = landing.pageConfig.blocks.map((b) => b.id)
  const remove = (existing ?? [])
    .map((row: { id: string }) => row.id)
    .filter((id: string) => !keep.includes(id))
  if (remove.length) {
    const { error: deleteError } = await supabase
      .from('landing_blocks')
      .delete()
      .in('id', remove)
    if (deleteError) {
      throw new Error(
        `No se han podido eliminar los bloques borrados: ${deleteError.message}`,
      )
    }
  }
  if (landing.pageConfig.blocks.length) {
    const { error: blocksError } = await supabase
      .from('landing_blocks')
      .upsert(
        landing.pageConfig.blocks.map((block, index) =>
          blockRow(block, landing.id, landing.organizationId, index),
        ),
        { onConflict: 'id' },
      )
    if (blocksError) {
      throw new Error(
        `No se han podido guardar los bloques: ${blocksError.message}`,
      )
    }
  }
  await refresh()
}

export async function setLandingStatus(
  landingId: string,
  status: LandingPage['status'],
): Promise<void> {
  const landing = getLanding(landingId)
  if (!landing) return
  const { error } = await supabase
    .from('landing_pages')
    .update({
      status: status === 'publicada' ? 'published' : 'draft',
      published_at: status === 'publicada' ? new Date().toISOString() : null,
    })
    .eq('id', landingId)
  if (error) throw new Error(error.message)
  await saveConfig(landing.organizationId, 'landing', landingId, {
    ...landingConfig(landing),
    status,
  })
  await refresh()
}

export async function duplicateLanding(
  landingId: string,
): Promise<LandingPage | undefined> {
  const original = getLanding(landingId)
  if (!original) return undefined
  const copy: LandingPage = {
    ...(JSON.parse(JSON.stringify(original)) as LandingPage),
    id: newId('landing'),
    name: `${original.name} (copia)`,
    slug: `${original.slug}-copia-${Date.now().toString(36)}`,
    status: 'borrador',
    views: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  }
  copy.pageConfig.blocks = copy.pageConfig.blocks.map((block) => ({
    ...block,
    id: newId('block'),
  }))
  const { error } = await supabase.from('landing_pages').insert(landingRow(copy))
  if (error) throw new Error(error.message)
  await saveLanding(copy)
  return copy
}

/** Borrado lógico: la landing pasa a la papelera de landings. */
export async function deleteLanding(landingId: string): Promise<void> {
  const { error } = await supabase
    .from('landing_pages')
    .update({ deleted_at: new Date().toISOString(), status: 'draft' })
    .eq('id', landingId)
  if (error) throw new Error(error.message)
  await refresh()
}

export async function restoreLanding(landingId: string): Promise<void> {
  const { error } = await supabase
    .from('landing_pages')
    .update({ deleted_at: null })
    .eq('id', landingId)
  if (error) throw new Error(error.message)
  await refresh()
}
